import {
  INITIAL_RESAMPLER_STATE,
  LIVE_PCM_SAMPLE_RATE,
  resamplePcm,
  type ResamplerState,
} from './pcm';

const PROCESSOR_NAME = 'diagnocat-pcm-capture';

const RESUME_TIMEOUT_MS = 1500;

// The processor only batches the first input channel and posts it to the main
// thread, where the tested resampler runs. A batch of 2048 frames is about
// 43 ms at 48 kHz, which keeps message traffic low without adding latency.
const PROCESSOR_SOURCE = `
class PcmCaptureProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.batch = new Float32Array(2048);
    this.size = 0;
    this.finished = false;
    this.port.onmessage = (event) => {
      if (event.data === 'flush') {
        this.finished = true;
        if (this.size) {
          this.port.postMessage(this.batch.slice(0, this.size));
        }
        this.size = 0;
        this.port.postMessage('flushed');
      }
    };
  }
  process(inputs) {
    if (this.finished) {
      return true;
    }
    const channel = inputs[0] && inputs[0][0];
    if (!channel) {
      return true;
    }
    for (let index = 0; index < channel.length; index += 1) {
      this.batch[this.size] = channel[index];
      this.size += 1;
      if (this.size === this.batch.length) {
        this.port.postMessage(this.batch.slice(0));
        this.size = 0;
      }
    }
    return true;
  }
}
registerProcessor('${PROCESSOR_NAME}', PcmCaptureProcessor);
`;

export type PcmCaptureListener = (samples: Int16Array) => void;

// Captures the recording's microphone stream as 24 kHz PCM16 in parallel with
// MediaRecorder, which keeps producing the uploaded audio unchanged.
export class PcmCapture {
  private context: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private node: AudioWorkletNode | null = null;
  private sink: GainNode | null = null;
  private resampler: ResamplerState = INITIAL_RESAMPLER_STATE;
  private paused = false;
  private onFlushed: (() => void) | null = null;

  static isSupported(): boolean {
    return (
      typeof AudioContext !== 'undefined' &&
      typeof AudioWorkletNode !== 'undefined'
    );
  }

  async start(
    stream: MediaStream,
    onSamples: PcmCaptureListener,
  ): Promise<void> {
    this.stop();
    // Reset before the awaits below, so a pause that comes while the context
    // starts is kept.
    this.paused = false;
    const context = new AudioContext();
    this.context = context;
    const moduleUrl = URL.createObjectURL(
      new Blob([PROCESSOR_SOURCE], { type: 'application/javascript' }),
    );
    try {
      await context.audioWorklet.addModule(moduleUrl);
    } finally {
      URL.revokeObjectURL(moduleUrl);
    }
    if (this.context !== context) {
      return;
    }
    // The context is created after the encounter request, outside the click.
    // Chrome and Safari still let a page that captures the microphone run it;
    // if a browser does not, the failure is reported instead of a live
    // transcript that silently stays empty.
    await Promise.race([
      context.resume().catch(() => undefined),
      new Promise((resolve) => window.setTimeout(resolve, RESUME_TIMEOUT_MS)),
    ]);
    if (this.context !== context) {
      return;
    }
    if (context.state !== 'running') {
      this.stop();
      throw new Error(`Live audio context is ${context.state}`);
    }
    this.resampler = INITIAL_RESAMPLER_STATE;
    this.source = context.createMediaStreamSource(stream);
    this.node = new AudioWorkletNode(context, PROCESSOR_NAME);
    // Chrome only runs a worklet that is pulled by the destination; the muted
    // gain keeps it running without playing the microphone back.
    this.sink = context.createGain();
    this.sink.gain.value = 0;
    this.node.port.onmessage = (event: MessageEvent<Float32Array | string>) => {
      if (event.data === 'flushed') {
        this.onFlushed?.();
        return;
      }
      if (typeof event.data === 'string') {
        return;
      }
      if (this.paused) {
        return;
      }
      const { output, state } = resamplePcm({
        input: event.data,
        inputRate: context.sampleRate,
        outputRate: LIVE_PCM_SAMPLE_RATE,
        state: this.resampler,
      });
      this.resampler = state;
      if (output.length) {
        onSamples(output);
      }
    };
    this.source.connect(this.node);
    this.node.connect(this.sink);
    this.sink.connect(context.destination);
  }

  pause(): void {
    this.paused = true;
  }

  resume(): void {
    this.paused = false;
  }

  async flush(): Promise<void> {
    const node = this.node;
    if (!node) {
      return;
    }
    await new Promise<void>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        this.onFlushed = null;
        reject(new Error('Live audio did not finish flushing'));
      }, RESUME_TIMEOUT_MS);
      this.onFlushed = () => {
        window.clearTimeout(timer);
        this.onFlushed = null;
        resolve();
      };
      node.port.postMessage('flush');
    });
  }

  stop(): void {
    if (this.node) {
      this.node.port.onmessage = null;
    }
    this.source?.disconnect();
    this.node?.disconnect();
    this.sink?.disconnect();
    this.source = null;
    this.node = null;
    this.sink = null;
    const context = this.context;
    this.context = null;
    if (context && context.state !== 'closed') {
      void context.close().catch(() => {
        /* ignore — the context is discarded either way */
      });
    }
  }
}
